/**
 * Итог автосверки Excel «Валовая прибыль» с выгрузкой из 1С.
 *
 * Один вид для страницы загрузки и для кабинета подрядчика: оба смотрят
 * на одну и ту же запись и не должны видеть разное. Расшифровка по товарам
 * приходит только администратору — компонент показывает её, если она есть.
 */

import type { GrossProfitCheck } from '../types'
import { Badge, Notice, TableWrap } from './ui'
import { Icon } from './Icon'
import { formatDate, formatDateTime, formatMoney } from '../format'

function signed(value: string | null): string {
  if (value === null) return '—'
  const number = Number(value)
  if (number === 0) return '0'
  return `${number > 0 ? '+' : '−'}${formatMoney(Math.abs(number), true)}`
}

export function GrossProfitCheckView({ check, compact = false }: { check: GrossProfitCheck; compact?: boolean }) {
  const pairs = check.pairs.filter((pair) => pair.file !== null)

  return (
    <div className="stack" style={{ gap: 12 }}>
      <Notice tone={check.is_ok ? 'good' : 'bad'} icon={check.is_ok ? 'check' : undefined}>
        <strong>{check.is_ok ? 'Сошлось.' : 'Не сошлось.'}</strong>{' '}
        {check.verdict.replace(/^(Сошлось|Не сошлось): /, '')}
        <div className="tiny muted" style={{ marginTop: 4 }}>
          {formatDate(check.period.from)} — {formatDate(check.period.to)} · база {check.source_database_id}
          {' '}· допуск {String(check.tolerance_percent).replace('.', ',')}% · {check.file_name ?? 'файл'} ·{' '}
          {formatDateTime(check.checked_at)}
        </div>
      </Notice>

      <TableWrap cards>
        <table className="data">
          <thead>
            <tr>
              <th>Показатель</th>
              <th>Вариант нашей стороны</th>
              <th className="num">В файле</th>
              <th className="num">У нас</th>
              <th className="num">Разница</th>
              <th>Итог</th>
            </tr>
          </thead>
          <tbody>
            {pairs.flatMap((pair) =>
              pair.variants
                // В компактном виде — только сошедшийся или ближайший вариант.
                .filter((variant) => !compact || variant.key === (pair.matched ?? pair.closest))
                .map((variant, index) => (
                  <tr key={`${pair.key}:${variant.key}`}>
                    <td>
                      {index === 0 || compact ? (
                        <>
                          <div>{pair.title}</div>
                          {!pair.required && <div className="tiny muted">подсказка, в итог не входит</div>}
                        </>
                      ) : null}
                    </td>
                    <td className="small">{variant.title}</td>
                    <td className="num">{formatMoney(pair.file, true)}</td>
                    <td className="num">{formatMoney(variant.ours, true)}</td>
                    <td className="num">
                      {signed(variant.diff)}
                      {variant.diff_percent !== null && variant.diff_percent !== 0 && (
                        <div className="tiny muted">{String(variant.diff_percent).replace('.', ',')}%</div>
                      )}
                    </td>
                    <td>
                      {variant.is_ok ? (
                        <Badge tone="good">сошлось</Badge>
                      ) : variant.key === pair.closest ? (
                        <Badge tone="warn">ближе всего</Badge>
                      ) : (
                        <span className="tiny muted">нет</span>
                      )}
                    </td>
                  </tr>
                )),
            )}
          </tbody>
        </table>
      </TableWrap>

      {!compact && (
        <div className="tiny muted">
          {pairs.map((pair) => pair.note).filter(Boolean).join(' · ')}. Разница — «в файле» минус «у нас».
          Наша сторона — проведённые, не удалённые реализации по всем организациям, как в «Сколько чего легло».
        </div>
      )}

      {check.items && check.items.length > 0 && (
        <details className="caveat caveat--details">
          <summary>
            <Icon name="search" className="caveat__icon" />
            Где разошлось: <span className="caveat__count">{check.items.length}</span> позиций с наибольшей разницей
            <Icon name="chevron-down" />
          </summary>
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Номенклатура</th>
                  <th className="num">Выручка в файле</th>
                  <th className="num">У нас</th>
                  <th className="num">Разница</th>
                  <th className="num">Себестоимость в файле</th>
                  <th className="num">У нас</th>
                  <th className="num">Разница</th>
                </tr>
              </thead>
              <tbody>
                {check.items.map((item) => (
                  <tr key={item.name}>
                    <td>
                      {item.name}
                      {item.only && (
                        <div className="tiny muted">{item.only === 'file' ? 'есть только в файле' : 'есть только в выгрузке'}</div>
                      )}
                    </td>
                    <td className="num">{formatMoney(item.file_revenue, true)}</td>
                    <td className="num">{formatMoney(item.our_revenue, true)}</td>
                    <td className="num">{signed(item.revenue_diff)}</td>
                    <td className="num">{formatMoney(item.file_cost, true)}</td>
                    <td className="num">{formatMoney(item.our_cost, true)}</td>
                    <td className="num">{signed(item.cost_diff)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
          <div className="tiny muted" style={{ padding: '8px 14px 12px' }}>
            Сопоставление по наименованию: регистр, пробелы и знаки препинания не учитываются, характеристика
            приклеивается к наименованию, как в отчёте 1С.
          </div>
        </details>
      )}
    </div>
  )
}
